import { DOCX_ERRORS, DOCX_LIMITS, DOCX_RENDER_OPTIONS } from './constants';
import { DocxPreviewError } from './DocxPreviewError';
import { sanitizeDocx } from './sanitizeDocx';
import type { ISanitizedDocx } from './sanitizeDocx';
import { validateDocx } from './validateDocx';

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new DocxPreviewError(DOCX_ERRORS.timeout)),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const parseAndRender = async (blob: Blob): Promise<Node[]> => {
  const { parseAsync, renderDocument } = await import('docx-preview');
  const doc = await parseAsync(blob, DOCX_RENDER_OPTIONS);
  return renderDocument(doc, DOCX_RENDER_OPTIONS);
};

/** Validates, renders and sanitizes a .docx; the result is safe to embed in a sandboxed iframe. */
export const renderDocx = async (blob: Blob): Promise<ISanitizedDocx> => {
  await validateDocx(blob);
  const nodes = await withTimeout(
    parseAndRender(blob),
    DOCX_LIMITS.renderTimeoutMs
  );
  return sanitizeDocx(nodes);
};
